import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try {
      const raw = localStorage.getItem("et_user");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });
  const [organization, setOrganization] = useState(() => {
    try {
      const raw = localStorage.getItem("et_org");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(true);

  const persist = (u, o) => {
    if (u) localStorage.setItem("et_user", JSON.stringify(u));
    else localStorage.removeItem("et_user");
    if (o) localStorage.setItem("et_org", JSON.stringify(o));
    else localStorage.removeItem("et_org");
    setUser(u);
    setOrganization(o);
  };

  const hydrate = useCallback(async () => {
    const token = localStorage.getItem("et_token");
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const [meRes, orgRes] = await Promise.all([api.get("/auth/me"), api.get("/org/me")]);
      persist(meRes.data, orgRes.data);
    } catch {
      localStorage.removeItem("et_token");
      persist(null, null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    hydrate();
  }, [hydrate]);

  const login = async (email, password) => {
    const res = await api.post("/auth/login", { email, password });
    localStorage.setItem("et_token", res.data.token);
    persist(res.data.user, res.data.organization);
    return res.data.user;
  };

  const register = async (payload) => {
    const res = await api.post("/auth/register", payload);
    localStorage.setItem("et_token", res.data.token);
    persist(res.data.user, res.data.organization);
    return res.data.user;
  };

  const logout = () => {
    localStorage.removeItem("et_token");
    persist(null, null);
  };

  return (
    <AuthContext.Provider value={{ user, organization, loading, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
